
import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import SuperAdminRestaurantsTab from './superadmin/SuperAdminRestaurantsTab';
import SuperAdminUsersTab from './superadmin/SuperAdminUsersTab';
import SuperAdminPermissionsTab from './superadmin/SuperAdminPermissionsTab';
import SuperAdminRolesTab from './superadmin/SuperAdminRolesTab';
import SuperAdminBillingTab from './superadmin/SuperAdminBillingTab';
import SuperAdminOnboardingTab from './superadmin/SuperAdminOnboardingTab';
import SuperAdminEmailDomainsTab from './superadmin/SuperAdminEmailDomainsTab';

interface SuperAdmin {
  loading: boolean;
  users: any[];
  restaurants: any[];
  organisations: any[];
  permissions: {
    organisationAccess: any[];
    restaurantAccess: any[];
  };
}

const SuperAdminPanel = () => {
  const [data, setData] = useState<SuperAdmin>({
    loading: true,
    users: [],
    restaurants: [],
    organisations: [],
    permissions: {
      organisationAccess: [],
      restaurantAccess: []
    }
  });
  
  // Fetch all necessary data for super admin
  useEffect(() => {
    const fetchSuperAdminData = async () => {
      try {
        // Fetch all users
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .order('name');
          
        if (usersError) throw usersError;
        
        // Fetch all restaurants
        const { data: restaurants, error: restaurantsError } = await supabase
          .from('restaurants_master')
          .select('*')
          .order('restaurant_name');
          
        if (restaurantsError) throw restaurantsError;
        
        // Fetch all organisations
        const { data: organisations, error: orgsError } = await supabase
          .from('organisations')
          .select('*')
          .order('name');
          
        if (orgsError) throw orgsError;
        
        // Fetch organisation access permissions
        const { data: orgAccess, error: orgAccessError } = await supabase
          .from('user_organisation_access')
          .select('*');
          
        if (orgAccessError) throw orgAccessError;
        
        // Fetch restaurant access permissions
        const { data: restAccess, error: restAccessError } = await supabase
          .from('user_restaurant_access')
          .select('*');
          
        if (restAccessError) throw restAccessError;
        
        setData({
          loading: false,
          users: users || [],
          restaurants: restaurants || [],
          organisations: organisations || [],
          permissions: {
            organisationAccess: orgAccess || [],
            restaurantAccess: restAccess || []
          }
        });
      } catch (error) {
        console.error('Error fetching super admin data:', error);
        toast({
          title: "Failed to load data",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
        setData(prev => ({ ...prev, loading: false }));
      }
    };
    
    fetchSuperAdminData();
  }, []);
  
  if (data.loading) {
    return <div className="p-8 text-center">Loading super admin panel...</div>;
  }

  return (
    <div className="space-y-8">
      <Tabs defaultValue="users">
        <TabsList className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap h-auto w-full mb-6 p-1">
          <TabsTrigger value="users" className="sm:flex-[1_0_auto]">Users</TabsTrigger>
          <TabsTrigger value="restaurants" className="sm:flex-[1_0_auto]">Restaurants & Organisations</TabsTrigger>
          <TabsTrigger value="permissions" className="sm:flex-[1_0_auto]">Access Permissions</TabsTrigger>
          <TabsTrigger value="roles" className="sm:flex-[1_0_auto]">Role Management</TabsTrigger>
          <TabsTrigger value="billing" className="sm:flex-[1_0_auto]">Billing</TabsTrigger>
          <TabsTrigger value="onboarding" className="sm:flex-[1_0_auto]">Onboarding</TabsTrigger>
          <TabsTrigger value="email-domains" className="sm:flex-[1_0_auto]">Email Domains</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminUsersTab 
              users={data.users} 
              organisations={data.organisations}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="restaurants" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminRestaurantsTab 
              restaurants={data.restaurants} 
              organisations={data.organisations}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="permissions" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminPermissionsTab 
              users={data.users}
              restaurants={data.restaurants}
              organisations={data.organisations}
              permissions={data.permissions}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="roles" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminRolesTab 
              organisations={data.organisations}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="billing" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminBillingTab 
              organisations={data.organisations}
              restaurants={data.restaurants}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="onboarding" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminOnboardingTab
              organisations={data.organisations}
            />
          </div>
        </TabsContent>

        <TabsContent value="email-domains" className="space-y-4 overflow-hidden">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <SuperAdminEmailDomainsTab
              organisations={data.organisations}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdminPanel;
